import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  Optional,
  Self,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ContentChild,
  HostBinding
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SelectDropDownModule } from '../../ngx-select-dropdown/ngx-select-dropdown.module';
import { JtDatepickerModule } from '../../../_components/jt-datepicker/jt-datepicker.module';
import { AppToggleSwitchModule } from '../../app-toggle-switch/app-toggle-switch.module';

export const JtFieldType = {
  Text: 'text',
  Email: 'email',
  Number: 'number',
  Textarea: 'textarea',
  Select: 'select',
  Dropdown: 'dropdown',
  Datepicker: 'datepicker',
  Toggle: 'toggle',
  File: 'file'
} as const;

export type JtFormFieldType = typeof JtFieldType[keyof typeof JtFieldType];

/** Auto-incrementing counter to generate unique field IDs for label/input association */
let nextFieldId = 0;

/**
 * JtFormFieldComponent — Smart form field wrapper.
 *
 * MODE 1 — Built-in control (type provided):
 *   <jt-form-field label="FieldLabel" type="text" formControlName="myField"
 *                  [fieldCols]="6" [requiredError]="'Error.Required'">
 *   </jt-form-field>
 *
 * MODE 2 — Custom content (type omitted, use ng-content for dropdown/datepicker/etc.):
 *   <jt-form-field label="Department" [fieldCols]="6">
 *     <app-ngx-select-dropdown formControlName="deptId" [config]="deptConfig">
 *     </app-ngx-select-dropdown>
 *   </jt-form-field>
 *
 * Supported built-in types: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'dropdown' | 'datepicker' | 'toggle' | 'file'
 */
@Component({
  selector: 'jt-form-field',
  templateUrl: './jt-form-field.component.html',
  styleUrls: ['./jt-form-field.component.css'],
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    FormsModule, 
    TranslateModule,
    SelectDropDownModule,
    JtDatepickerModule,
    AppToggleSwitchModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JtFormFieldComponent implements ControlValueAccessor, OnInit {

  // ─── Label & Layout ─────────────────────────────────────────────────────────
  /** Translation key or plain text for the field label */
  @Input() label: string = '';

  /** Number of Bootstrap columns for the label (default 2) */
  @Input() labelCols: number = 2;

  /** Number of Bootstrap columns for the field (default 6) */
  @Input() fieldCols: number = 6;

  /** Whether this field is inside a <jt-form-row> (no outer row wrapper) */
  @Input() inline: boolean = false;

  /**
   * Whether the field is required.
   * Adds aria-required="true" to the control and a visual asterisk to the label.
   * Note: this is for UI display only — validators must still be set on the FormControl.
   */
  @Input() required: boolean = false;

  /**
   * Unique ID used to link <label for=""> to <input id="">.
   * Auto-generated if not provided. Override only if you need a fixed id.
   */
  @Input() fieldId: string = `jt-field-${++nextFieldId}`;

  /**
   * Optional ID of a projected control (Mode 2).
   * Use this when projecting a custom control so the label can still target
   * the real interactive element instead of just the wrapper container.
   */
  @Input() projectedControlId: string = '';

  // ─── Control Type ───────────────────────────────────────────────────────────
  /**
   * Type of built-in control to render.
   * Leave undefined to use ng-content (custom control projection).
   */
  @Input() type?: JtFormFieldType;

  // ─── Mode 1 Specific Configurations ──────────────────────────────────────────
  
  // Select Native
  @Input() selectOptions: { value: any, label: string }[] = [];
  
  // Dropdown (ngx-select)
  @Input() dropdownConfig: any = {};
  @Input() dropdownOptions: any[] = [];
  @Input() dropdownMultiple: boolean = false;

  // Toggle switch
  @Input() toggleTrueFalseValue: boolean = true;
  @Input() toggleTrueValue: string = 'YES';
  @Input() toggleFalseValue: string = 'NO';
  // Datepicker
  @Input() datepickerMinDate?: Date;
  @Input() datepickerMaxDate?: Date;
  @Input() datepickerServerFormat: string = 'YYYY-MM-DD';

  /** Number of rows for textarea (only used when type='textarea') */
  @Input() rows: number = 3;

  /** Native minlength attribute for text/email/number/textarea */
  @Input() minLength: number | null = null;

  /** Native maxlength attribute for text/email/number/textarea */
  @Input() maxLength: number | null = null;

  /** Comma-separated list of accepted file types (only used when type='file'). e.g. '.pdf,.docx' */
  @Input() accept: string = '';

  /** Whether the file input allows multiple file selection (only used when type='file') */
  @Input() multiple: boolean = false;

  /** Placeholder text for the input */
  @Input() placeholder: string = '';

  /** Manual override for data-testid. Defaults to formControlName. */
  @Input() testId: string = '';

  /** Manual override for CSS class on the internal control. Appended to formControlName. */
  @Input() customClass: string = '';

  /** Whether the field is disabled */
  @Input() disabled: boolean = false;

  /** Whether the field is readonly */
  @Input() readonly: boolean = false;

  // ─── Validation Error Messages ───────────────────────────────────────────────
  /** Translation key shown when the 'required' validator fails */
  @Input() requiredError: string = 'Common.Required';

  /** Translation key shown when the 'email' validator fails */
  @Input() emailError: string = 'Common.InvalidEmail';

  /** Translation key shown when the 'minlength' validator fails */
  @Input() minLengthError: string = '';

  /** Translation key shown when the 'maxlength' validator fails */
  @Input() maxLengthError: string = '';

  /** Translation key shown when the 'pattern' validator fails */
  @Input() patternError: string = '';

  /** Translation key shown for any other custom validation error */
  @Input() customError: string = '';

  /** Enables showing validation errors even if the field hasn't been touched yet */
  @Input() isSubmitted: boolean = false;

  /** Grabs the projected ngControl from custom components inside ng-content (Mode 2) */
  @ContentChild(NgControl) projectedControl?: NgControl;

  // ─── Output Events ───────────────────────────────────────────────────────────
  /** Emits the new value whenever the internal control changes. */
  @Output() byValueChange = new EventEmitter<any>();

  // ─── Internal State ──────────────────────────────────────────────────────────
  value: any = '';

  /** Manual fallback for the control name. Use only for action-only fields without a bound form control. */
  @Input() controlName: string = '';

  /** Extract the name from the actual bound control first, then fall back to manual metadata. */
  get computedControlName(): string {
    const name = this.ngControl?.name || this.projectedControl?.name || this.controlName || '';
    return name.toString();
  }

  @HostBinding('class')
  get hostClasses(): string {
    const base = this.inline ? '' : 'form-group row';
    const containerClass = this.computedControlName ? `container-${this.computedControlName}` : '';
    return `${base} ${containerClass}`.trim();
  }

  @HostBinding('style.display')
  get hostDisplay(): string | null {
    return this.inline ? 'contents' : null;
  }

  @HostBinding('attr.data-testid')
  get hostTestId(): string {
    return this.computedTestId ? `container-${this.computedTestId}` : '';
  }

  /** 
   * Standardized HTML ID for the control.
   * Priority: actual control name > manual fallback > auto-generated fieldId.
   */
  get computedId(): string {
    return this.computedControlName || this.fieldId;
  }

  /** Computed ID for the error message container — used by aria-describedby */
  get errorId(): string {
    return `${this.computedId}-error`;
  }

  /** Computed data-testid for the internal control */
  get computedTestId(): string {
    return this.testId || this.computedControlName;
  }

  /** Computed CSS classes for the internal control (input, dropdown, etc.) */
  get computedInternalClass(): string {
    const nameClass = this.computedControlName;
    return `${nameClass} ${this.customClass}`.trim();
  }

  private _onChange: (value: any) => void = () => { };
  private _onTouched: () => void = () => { };

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private cdr: ChangeDetectorRef
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void { }

  /** Returns true when built-in control should be rendered */
  get hasBuiltInControl(): boolean {
    if (!this.type) return false;
    return (Object.values(JtFieldType) as string[]).includes(this.type as string);
  }

  /** Returns the AbstractControl from the parent FormGroup */
  get control(): AbstractControl | null {
    if (this.hasBuiltInControl) {
      return this.ngControl?.control ?? null;
    }
    // Mode 2: Custom components inside ng-content
    return this.projectedControl?.control ?? null;
  }

  /** Returns true when validation errors should be shown */
  get showErrors(): boolean {
    const ctrl = this.control;
    if (!ctrl) return false;
    return ctrl.invalid && (this.isSubmitted || ctrl.dirty || ctrl.touched);
  }

  /** Returns the active validation errors object */
  get errors(): ValidationErrors | null {
    return this.control?.errors ?? null;
  }

  writeValue(value: any): void {
    let processedValue = value
    
    if (this.type === 'dropdown' && !this.dropdownMultiple && Array.isArray(value) && value.length > 0) {
      processedValue = value[0];
    }
    this.value = processedValue ?? '';
    if (processedValue !== value && this.ngControl?.control) {
      this.ngControl.control.setValue(processedValue, { emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInputChange(event: any): void {
    // If it's a native DOM event from an <input>
    if (event && event.target !== undefined) {
      this.value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    } else {
      // If it's an ngModelChange passing the value directly (e.g. from dropdown)
      this.value = event;
    }
    
    this._onChange(this.value);
    this.byValueChange.emit(this.value);
  }

  onFileChange(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.value = files;
    this._onChange(files);
    this.byValueChange.emit(files);
  }

  onBlur(): void {
    this._onTouched();
  }
}
